/* eslint-disable prettier/prettier */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Grid, IconButton, Modal, Typography} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import styles, {ORANGE_COLOR} from '../../styles';
import {Control, FieldValues, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {Player} from '../../interfaces';
import {SessionTypes} from '../../enums';
import FormCheckbox from './FormCheckbox';
import SessionTypeFilter, { SessionTypeFilters } from './FilterComponents/SessionTypes';
import SessionDatesFilter from './FilterComponents/SessionDates';
import PendingDataFilter from './FilterComponents/PendingData';
import SessionMediaFilter from './FilterComponents/SessionMedia';

export interface TeamSessionFilterForm {
  dateRange: {
    startDate: Date | null;
    endDate: Date | null;
  } | null;
  sessionTypes: SessionTypeFilters;
  // players: object with dynamic number of entries
  players: Record<string, boolean>;
  dataStatus: {
    pending: boolean;
    processed: boolean;
  };
  sessionMedia: {
    hasVideo: boolean;
  };
}

interface Props {
  currentFilters?: TeamSessionFilterForm;
  onFilterUpdate: (newFilters: TeamSessionFilterForm) => void;
  players: Player[];
  teamId?: number;
}

const defaultFilterValue: TeamSessionFilterForm = {
  dateRange: null,
  sessionTypes: {
    [SessionTypes.game]: false,
    [SessionTypes.practice]: false,
    [SessionTypes.skills]: false,
  },
  players: {},
  dataStatus: {
    pending: false,
    processed: false,
  },
  sessionMedia: {
    hasVideo: false,
  },
};

const TeamSessionFilter: React.FC<Props> = ({
  currentFilters,
  onFilterUpdate,
  players,
  teamId,
}) => {
  const {t} = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const {handleSubmit, reset, control, setValue} =
    useForm<TeamSessionFilterForm>({
      defaultValues: currentFilters || defaultFilterValue,
    });

  const onApply = useCallback(
    (data: TeamSessionFilterForm) => {
      onFilterUpdate(data);
      setShowModal(false);
    },
    [onFilterUpdate],
  );

  const onCancel = useCallback(() => {
    reset(currentFilters || defaultFilterValue);
    setShowModal(false);
  }, [currentFilters, reset]);

  const onClear = useCallback(() => {
    reset(defaultFilterValue);
    onFilterUpdate(defaultFilterValue);
    setShowModal(false);
  }, [reset, onFilterUpdate]);

  const filterIconActive = useMemo(() => {
    if (!currentFilters) {
      return false;
    }
    if (currentFilters.dateRange?.startDate || currentFilters.dateRange?.endDate) {
      return true;
    }
    if (Object.values(currentFilters.sessionTypes).some(val => val) || Object.values(currentFilters.players).some(val => val)) {
      return true;
    }
    if (currentFilters.dataStatus.pending || currentFilters.dataStatus.processed) {
      return true;
    }
    if (currentFilters.sessionMedia.hasVideo) {
      return true;
    }
    return false;
  }, [currentFilters]);

  useEffect(() => {
    reset(currentFilters || defaultFilterValue);
  }, [currentFilters, reset, teamId]);

  return (
    <>
      <IconButton
        sx={
          filterIconActive
            ? {
                color: ORANGE_COLOR,
              }
            : {}
        }
        onClick={() => setShowModal(true)}>
        <FilterAltIcon fontSize="large" />
      </IconButton>
      <Modal
        open={showModal}
        onClose={() => {
          setShowModal(false);
        }}>
        <form onSubmit={handleSubmit(onApply)} className="filterform">
          <Box sx={styles.modal}>
            <Typography variant="h3" sx={{mb: 2}}>
              {t('sessionFilter.label')}
            </Typography>
            <Box sx={{padding: 2}}>
              <SessionDatesFilter
                control={control as unknown as Control<FieldValues>}
                setValue={setValue}
              />
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <SessionTypeFilter
                    control={control as unknown as Control<FieldValues>}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <PendingDataFilter
                    control={control as unknown as Control<FieldValues>}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <SessionMediaFilter
                    control={control as unknown as Control<FieldValues>}
                  />
                </Grid>
              </Grid>
              {players?.length && (
                <Box sx={{mb: 2}}>
                  <Typography variant="h4" sx={{mb: 2}}>
                    {t('sessionFilter.players')}
                  </Typography>
                  <Grid container columnSpacing={2}>
                    {players.map(player => (
                      <Grid key={player.id} item xs={12} md={4}>
                        <FormCheckbox
                          key={player.id}
                          control={control as unknown as Control<FieldValues>}
                          name={`players.${player.id}`}
                          label={`${player.profile?.firstName} ${player.profile?.lastName}`}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
            <Box sx={{textAlign: 'right'}}>
              <Button sx={{mr: 2}} onClick={onCancel}>
                {t('general.cancel')}
              </Button>
              <Button sx={{mr: 2}} variant="outlined" onClick={onClear}>
                {t('general.reset')}
              </Button>
              <Button variant="contained" type="submit">
                {t('general.apply')}
              </Button>
            </Box>
          </Box>
        </form>
      </Modal>
    </>
  );
};

export default TeamSessionFilter;